import React from 'react';
import { Box, Chip, ChipProps } from '@mui/material';
import { DAWInfo, getDAW } from '../../models/DAWs';
import { findDAWKeyByName } from '../../models/DAWs';
import { borderRadius } from '../../../theme/foundations/shape';

const INFO_CHIP_HEIGHTS = {
  small: 24,
  medium: 36.75,
  large: 48,
} as const;

const INFO_CHIP_LABEL_PADDING_X = {
  small: 1,
  medium: 2,
  large: 1.75,
} as const;

const INFO_CHIP_FONT_SIZES = {
  small: '0.75rem',
  medium: '13px',
  large: '1.125rem',
} as const;

const INFO_CHIP_ICON_MARGINS = {
  small: { marginLeft: '8px', marginRight: '-4px' },
  medium: { marginLeft: '10px', marginRight: '-6px' },
  large: { marginLeft: '12px', marginRight: '-6px' },
} as const;

export interface InfoChipProps {
  label: string;
  value?: string | number;
  icon?: React.ReactElement;
  color?: ChipProps['color'];
  variant?: ChipProps['variant'];
  size?: 'small' | 'medium' | 'large';
  borderColor?: string;
  valueColor?: string;
  cautionTape?: boolean; // Animated caution tape border effect
  sx?: ChipProps['sx'];
}

/**
 * Global reusable chip component for displaying information
 * Supports DAW labels with icons, statistics, and general purpose chips
 */
const InfoChip: React.FC<InfoChipProps> = ({
  label,
  value,
  icon,
  color = 'default',
  variant = 'soft',
  size = 'small',
  borderColor,
  valueColor,
  cautionTape = false,
  sx,
}) => {
  const chipContent = value ? (
    <Box component="span" display="flex" alignItems="center" gap={0.5}>
      <Box
        component="span"
        sx={{
          color: valueColor || 'inherit',
          fontWeight: 600,
        }}
      >
        {value}
      </Box>
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {label}
      </Box>
    </Box>
  ) : (
    label
  );

  if (cautionTape) {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
        }}
      >
        <Chip
          label={chipContent}
          {...(icon && { icon })}
          color={color}
          variant={variant}
          size={size === 'large' ? 'medium' : size}
          sx={{
            height: INFO_CHIP_HEIGHTS[size],
            borderWidth: 3,
            borderRadius: `${borderRadius.small}px`,
            borderStyle: 'solid',
            borderColor: 'warning.main',
            position: 'relative',
            background: 'transparent',
            '& .MuiChip-label': {
              position: 'relative',
              zIndex: 1,
              fontSize: INFO_CHIP_FONT_SIZES[size],
              lineHeight: size === 'medium' ? '24.75px' : undefined,
              fontWeight: 500,
              px: INFO_CHIP_LABEL_PADDING_X[size],
            },
            '& .MuiChip-icon': {
              position: 'relative',
              zIndex: 1,
              ...INFO_CHIP_ICON_MARGINS[size],
            },
            ...sx,
          }}
        />
      </Box>
    );
  }

  return (
    <Chip
      label={chipContent}
      {...(icon && { icon })}
      color={color}
      variant={variant}
      size={size === 'large' ? 'medium' : size}
      sx={{
        height: INFO_CHIP_HEIGHTS[size],
        borderWidth: variant === 'filled' ? 0 : 1,
        borderRadius: `${borderRadius.small}px`,
        ...(borderColor && {
          borderColor,
          '&:hover': {
            borderColor,
          },
        }),
        '& .MuiChip-label': {
          fontSize: INFO_CHIP_FONT_SIZES[size],
          lineHeight: size === 'medium' ? '24.75px' : undefined,
          fontWeight: 500,
          px: INFO_CHIP_LABEL_PADDING_X[size],
        },
        '& .MuiChip-icon': {
          ...INFO_CHIP_ICON_MARGINS[size],
        },
        ...sx,
      }}
    />
  );
};

/**
 * DAW-specific chip that includes the DAW icon
 */
export interface DAWInfoChipProps {
  dawType: string; // DAWType or DAW name
  count?: number; // Optional count to display after the DAW name
  size?: 'small' | 'medium' | 'large';
  variant?: ChipProps['variant'];
  sx?: ChipProps['sx'];
}

export const DAWInfoChip: React.FC<DAWInfoChipProps> = ({
  dawType,
  count,
  size = 'small',
  variant = 'outlined',
  sx,
}) => {
  // Map DAWType to DAWKey
  const dawTypeToKey: Record<string, string> = {
    ableton: 'ableton-live',
    logic: 'logic-pro',
    protools: 'pro-tools',
    flstudio: 'fl-studio',
    cubase: 'cubase',
    reaper: 'reaper',
    stems: 'stems',
    unknown: 'unknown',
  };

  // DAW color mapping (preserved from original DAWLogo)
  const DAW_COLORS: Record<string, ChipProps['color']> = {
    ableton: 'default',
    logic: 'secondary',
    protools: 'info',
    cubase: 'success',
    flstudio: 'warning',
    reaper: 'error',
    stems: 'default',
    unknown: 'default',
  };

  // Try to find DAW by type first, then by name
  let dawKey: string | undefined = dawTypeToKey[dawType.toLowerCase()];
  if (!dawKey) {
    dawKey = findDAWKeyByName(dawType);
  }

  let dawInfo: DAWInfo | undefined;
  if (dawKey && dawKey !== 'unknown') {
    try {
      // Type assertion needed because DAWKey is more specific than string
      dawInfo = getDAW(dawKey as Parameters<typeof getDAW>[0]);
    } catch {
      // DAW not found, will use fallback
    }
  }

  // Get the normalized DAW type for color lookup
  const normalizedDawType = dawType.toLowerCase();
  const dawColor = DAW_COLORS[normalizedDawType] || 'default';

  // Create icon element
  const iconElement = dawInfo ? (
    <Box
      sx={{
        width: size === 'small' ? 24 : size === 'medium' ? 28 : 44,
        height: size === 'small' ? 24 : size === 'medium' ? 28 : 44,
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {dawInfo.icon.icon ? (
        <Box
          component="img"
          src={dawInfo.icon.icon}
          alt={dawInfo.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '4px',
            background: dawInfo.icon.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize:
              size === 'small' ? '10px' : size === 'medium' ? '12px' : '20px',
            fontWeight: 'bold',
            color: 'white',
          }}
        >
          {dawInfo.icon.fallbackChar}
        </Box>
      )}
    </Box>
  ) : null;

  const dawName = dawInfo?.name || dawType;
  const label = count !== undefined ? `${dawName} ${count}` : dawName;

  return (
    <InfoChip
      label={label}
      {...(iconElement && { icon: iconElement })}
      color={dawColor}
      variant={variant}
      size={size}
      sx={sx}
    />
  );
};

export default InfoChip;
